// Platform-agnostic core: parsing + matching only.
// No storage, no UI, no platform APIs.
import {
  CATEGORY_NAMES,
  normalizeCategoryRules,
  resolveCategoryFromHost,
} from "../blocking/categoryModel";
export { CATEGORY_NAMES, normalizeCategoryRules };

export function normalizeHost(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";

  let s = raw.replace(/^\s*https?:\/\//i, "");
  s = s.replace(/^\s*www\./i, "");
  s = s.split(/[/?#]/)[0];
  s = s.trim().toLowerCase();
  s = s.replace(/\.+$/, "").replace(/\s+/g, "");
  return s;
}

function getHostFromUrl(urlLike) {
  const raw = String(urlLike || "").trim();
  if (!raw) return "";
  try {
    const u = new URL(/^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`);
    return normalizeHost(u.hostname);
  } catch {
    return normalizeHost(raw);
  }
}

function matchesBlocked(host, blockedHost) {
  if (!host || !blockedHost) return false;
  if (host === blockedHost) return true;
  return host.endsWith(`.${blockedHost}`);
}

export function resolveDomainCategory(hostLike) {
  const host = normalizeHost(hostLike);
  if (!host) return null;
  return resolveCategoryFromHost(host, {
    domainMatches: (a, b) => matchesBlocked(a, normalizeHost(b)),
  });
}

export function parseBlockListText(text) {
  const lines = String(text || "")
    .split(/\r?\n|,/g)
    .map((x) => normalizeHost(x))
    .filter(Boolean);
  return Array.from(new Set(lines));
}

/** One keyword per line or comma-separated; matched case-insensitively anywhere in the URL. */
export function parseKeywordList(text) {
  return Array.from(
    new Set(
      String(text || "")
        .split(/\r?\n|,/g)
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

export function normalizeBlockList(list) {
  return Array.from(new Set((list || []).map(normalizeHost).filter(Boolean)));
}

/**
 * Returns the first matching decision for a URL using the same precedence as blocking:
 * allowlist host match -> keyword match -> blocklist host match -> allow.
 */
export function explainUrlDecision(urlLike, blockList, options = {}) {
  const allowList = options.allowList ?? [];
  const keywordBlockList = options.keywordBlockList ?? options.keywords ?? [];
  const categoryRules = normalizeCategoryRules(options.categoryRules);

  const host = getHostFromUrl(urlLike);
  const urlLower = String(urlLike || "").toLowerCase();
  const category = resolveDomainCategory(host);

  if (category) {
    const categoryRule = categoryRules[category] || "ignore";
    if (categoryRule === "allow") {
      return {
        blocked: false,
        reason: "category-allow",
        matchedRule: category,
      };
    }
    if (categoryRule === "block") {
      return {
        blocked: true,
        reason: "category-block",
        matchedRule: category,
      };
    }
  }

  for (const item of allowList || []) {
    const allowedHost = normalizeHost(item);
    if (allowedHost && host && matchesBlocked(host, allowedHost)) {
      return {
        blocked: false,
        reason: "allowlist",
        matchedRule: allowedHost,
      };
    }
  }

  for (const kw of keywordBlockList || []) {
    const k = String(kw || "").toLowerCase().trim();
    if (k && urlLower.includes(k)) {
      return {
        blocked: true,
        reason: "keyword",
        matchedRule: k,
      };
    }
  }

  if (!host) {
    return {
      blocked: false,
      reason: "no-match",
      matchedRule: null,
    };
  }

  for (const item of blockList || []) {
    const blockedHost = normalizeHost(item);
    if (matchesBlocked(host, blockedHost)) {
      return {
        blocked: true,
        reason: "blocklist",
        matchedRule: blockedHost,
      };
    }
  }

  return {
    blocked: false,
    reason: "no-match",
    matchedRule: null,
  };
}

/**
 * @param {string} urlLike
 * @param {string[]} blockList domain blocklist
 * @param {{ allowList?: string[], keywordBlockList?: string[] }} [options]
 * Order: allowlist (host) → keyword in full URL → domain blocklist.
 */
export function isUrlBlocked(urlLike, blockList, options = {}) {
  return explainUrlDecision(urlLike, blockList, options).blocked;
}

